/**
 * A Yjs provider with no server behind it.
 *
 * Speaks the same two-message protocol as the WebSocket relay — sync and
 * awareness — but over a mesh of direct DataChannels instead of through a
 * middle. Interface-compatible with y-websocket's provider where this app uses
 * it, so the rest of the editor does not know or care which one it has.
 */
import * as Y from 'yjs';
import * as syncProtocol from 'y-protocols/sync';
import * as awarenessProtocol from 'y-protocols/awareness';
import * as encoding from 'lib0/encoding';
import * as decoding from 'lib0/decoding';
import { Observable } from 'lib0/observable';
import { createMesh } from './mesh.js';
import { joinFloor } from './floor.js';

const MESSAGE_SYNC = 0;
const MESSAGE_AWARENESS = 1;

export class P2pProvider extends Observable {
  constructor(room, doc, { awareness = new awarenessProtocol.Awareness(doc), connect = true, useMesh = true, useFloor = true } = {}) {
    super();
    this.room = room;
    this.doc = doc;
    this.awareness = awareness;
    this.mesh = null;
    // The floor: p2p's universal fallback. WebRTC cannot cross a symmetric-NAT
    // pair, and that is precisely the case where two windows on different
    // networks would otherwise never meet.
    this.floor = null;
    this.floorPeers = new Set();
    this.useMesh = useMesh;
    this.useFloor = useFloor;
    // Windows of the same browser sync directly, without waiting on a tracker
    // round trip or an ICE handshake — the two-window demo is instant, and it
    // still works with the network unplugged.
    this.bc = null;
    // Which windows we can still reach over the browser channel. A peer may be
    // reachable both ways; losing one path is not losing the peer.
    this.bcPeers = new Set();
    this.synced = false;
    this.shouldConnect = connect;

    // Floor traffic is batched: a public MQTT broker rate-limits publishers, and
    // per-keystroke publishes get a fast typist silently dropped (observed live —
    // 3 characters arrived, 33 did not). Yjs updates merge losslessly, so a
    // window's worth becomes one frame. Direct paths stay per-keystroke.
    this._floorQueue = [];
    this._floorTimer = null;

    this._onUpdate = (update, origin) => {
      // Anything that arrived from a peer is already on its way to the others.
      if (origin === this) return;
      const encoder = encoding.createEncoder();
      encoding.writeVarUint(encoder, MESSAGE_SYNC);
      syncProtocol.writeUpdate(encoder, update);
      this.mesh?.broadcast(encoding.toUint8Array(encoder));
      try { this.bc?.postMessage(encoding.toUint8Array(encoder)); } catch { /* closed */ }
      if (this.floor) {
        this._floorQueue.push(update);
        this._floorTimer ??= setTimeout(() => this._flushFloor(), 80);
      }
    };

    this._floorAwareness = false;
    this._onAwareness = ({ added, updated, removed }) => {
      const changed = added.concat(updated, removed);
      const encoder = encoding.createEncoder();
      encoding.writeVarUint(encoder, MESSAGE_AWARENESS);
      encoding.writeVarUint8Array(
        encoder,
        awarenessProtocol.encodeAwarenessUpdate(this.awareness, changed),
      );
      this.mesh?.broadcast(encoding.toUint8Array(encoder));
      try { this.bc?.postMessage(encoding.toUint8Array(encoder)); } catch { /* closed */ }
      if (this.floor) {
        // Presence is a snapshot, not a log — only the latest state matters, so
        // the flush re-encodes it rather than queueing every intermediate one.
        this._floorAwareness = true;
        this._floorTimer ??= setTimeout(() => this._flushFloor(), 80);
      }
    };

    this._onUnload = () => {
      awarenessProtocol.removeAwarenessStates(this.awareness, [this.doc.clientID], 'unload');
      try { this.bc?.postMessage(JSON.stringify({ echo: 'bye', id: this.doc.clientID })); } catch { /* closed */ }
    };

    this.doc.on('update', this._onUpdate);
    this.awareness.on('update', this._onAwareness);
    addEventListener('beforeunload', this._onUnload);

    if (connect) this.connect();
  }

  /** One flush window's worth of floor traffic, as at most two frames. */
  _flushFloor() {
    this._floorTimer = null;
    if (!this.floor) { this._floorQueue = []; this._floorAwareness = false; return; }
    if (this._floorQueue.length) {
      const merged = this._floorQueue.length === 1
        ? this._floorQueue[0]
        : Y.mergeUpdates(this._floorQueue);
      this._floorQueue = [];
      const encoder = encoding.createEncoder();
      encoding.writeVarUint(encoder, MESSAGE_SYNC);
      syncProtocol.writeUpdate(encoder, merged);
      this.floor.send(encoding.toUint8Array(encoder));
    }
    if (this._floorAwareness) {
      this._floorAwareness = false;
      const encoder = encoding.createEncoder();
      encoding.writeVarUint(encoder, MESSAGE_AWARENESS);
      encoding.writeVarUint8Array(
        encoder,
        awarenessProtocol.encodeAwarenessUpdate(this.awareness, [...this.awareness.getStates().keys()]),
      );
      this.floor.send(encoding.toUint8Array(encoder));
    }
  }

  _handle(peer, bytes) {
    const decoder = decoding.createDecoder(bytes);
    const encoder = encoding.createEncoder();
    switch (decoding.readVarUint(decoder)) {
      case MESSAGE_SYNC: {
        encoding.writeVarUint(encoder, MESSAGE_SYNC);
        const type = syncProtocol.readSyncMessage(decoder, encoder, this.doc, this);
        // A step-2 reply means this peer has answered our state vector: whatever
        // it knew that we did not is now applied, which is what "synced" means
        // in a mesh with no authority to ask.
        if (type === syncProtocol.messageYjsSyncStep2 && !this.synced) {
          this.synced = true;
          this.emit('sync', [true]);
        }
        if (encoding.length(encoder) > 1) peer.send(encoding.toUint8Array(encoder));
        break;
      }
      case MESSAGE_AWARENESS:
        awarenessProtocol.applyAwarenessUpdate(this.awareness, decoding.readVarUint8Array(decoder), this);
        break;
    }
  }

  async connect() {
    if (this.mesh) return;
    this.shouldConnect = true;
    this.emit('status', [{ status: 'connecting' }]);

    // Same-browser windows first: no signalling, no ICE, no internet.
    this.bc = new BroadcastChannel(`echo:${this.room}`);
    const local = {
      send: (bytes) => { try { this.bc?.postMessage(bytes); } catch { /* closed */ } },
    };
    const hello = () => local.send(JSON.stringify({ echo: 'hello', id: this.doc.clientID }));

    this.bc.onmessage = (event) => {
      if (typeof event.data === 'string') {
        let note;
        try { note = JSON.parse(event.data); } catch { return; }
        if (note.echo === 'hello') {
          // A window that just opened does not know us yet; answer so the path
          // is known in both directions, then sync.
          if (!this.bcPeers.has(note.id)) { this.bcPeers.add(note.id); hello(); this._greet(local); }
          this.emit('status', [{ status: 'connected' }]);
        } else if (note.echo === 'bye') {
          this.bcPeers.delete(note.id);
        }
        return;
      }
      try { this._handle(local, new Uint8Array(event.data)); } catch (err) { this.emit('connection-error', [err]); }
    };

    hello();
    this._greet(local);

    if (this.useFloor) {
      try {
        const selfId = this.doc.clientID;
        this.floor = await joinFloor({
          room: this.room,
          selfId,
          onFrame: (from, bytes) => {
            const known = this.floorPeers.has(from);
            if (!known) {
              this.floorPeers.add(from);
              this.emit('status', [{ status: 'connected' }]);
              this.emit('peers', [{ peers: this._reachable() }]);
            }
            const peer = { send: (out) => this.floor?.send(out) };
            try {
              this._handle(peer, bytes);
              // A window we have just heard from for the first time needs our
              // state vector, or neither side ever offers one.
              if (!known) this._greet(peer);
            } catch (err) { this.emit('connection-error', [err]); }
          },
          onStatus: ({ relays }) => this.emit('floor', [{ relays }]),
        });
        this._greet({ send: (out) => this.floor?.send(out) });
      } catch (err) {
        this.emit('connection-error', [err]);
      }
    }

    try {
      if (!this.useMesh) return;
      this.mesh = await createMesh({
        room: this.room,
        selfId: this.doc.clientID,
        onPeer: (peer) => {
          peer.onMessage = (bytes) => {
            try { this._handle(peer, bytes); } catch (err) { this.emit('connection-error', [err]); }
          };
          this._greet(peer);
          this.emit('status', [{ status: 'connected' }]);
        },
        onLeave: (id) => {
          // Presence is per-connection here: no server holds it for us. But only
          // forget a peer we have genuinely lost — a window reachable over the
          // browser channel is still in the room even if its WebRTC leg died.
          if (!this.bcPeers.has(id)) {
            awarenessProtocol.removeAwarenessStates(this.awareness, [id], 'peer-left');
          }
          this.emit('status', [{ status: this._reachable() ? 'connected' : 'connecting' }]);
        },
        onStatus: () => this.emit('peers', [{ peers: this._reachable() }]),
      });
    } catch (err) {
      this.emit('connection-error', [err]);
      return;
    }

    if (!this.shouldConnect) { this.mesh.close(); this.mesh = null; }
  }

  /**
   * How we are actually reaching one peer, for the inspector.
   *
   * A peer can be reachable two ways at once; report the one carrying traffic —
   * the browser channel is the shorter path and wins when both exist.
   */
  async peerInfo(clientId) {
    if (this.bcPeers.has(clientId)) {
      return { path: 'broadcast', label: 'same browser', detail: 'BroadcastChannel — never leaves this machine', direct: true };
    }
    const peer = this.mesh?.get(clientId);
    if (!peer) {
      if (this.floorPeers.has(clientId)) {
        return {
          path: 'floor',
          label: 'via public relay (floor)',
          detail: `MQTT-over-WSS relay from p2p — used when WebRTC cannot cross the NAT. Sealed with a key derived from the room name; the broker sees an opaque topic and ciphertext. ${this.floor?.relays ?? 0} relay(s) up.`,
          direct: false,
        };
      }
      return { path: 'none', label: 'not connected', detail: 'no open channel to this window' };
    }
    const stats = await peer.stats();
    return { ...stats, label: stats.direct === false ? 'relayed WebRTC' : 'direct WebRTC', detail: 'WebRTC DataChannel, signalled over public trackers' };
  }

  /** Every window we can currently reach, by any path. */
  _reachable() {
    return (this.mesh?.size ?? 0) + this.bcPeers.size + this.floorPeers.size;
  }

  /** Opening move to anyone new: our state vector, then our presence. */
  _greet(peer) {
    const sync = encoding.createEncoder();
    encoding.writeVarUint(sync, MESSAGE_SYNC);
    syncProtocol.writeSyncStep1(sync, this.doc);
    peer.send(encoding.toUint8Array(sync));

    if (!this.awareness.getLocalState()) return;
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_AWARENESS);
    encoding.writeVarUint8Array(
      encoder,
      awarenessProtocol.encodeAwarenessUpdate(this.awareness, [this.doc.clientID]),
    );
    peer.send(encoding.toUint8Array(encoder));
  }

  disconnect() {
    this.shouldConnect = false;
    try {
      this.bc?.postMessage(JSON.stringify({ echo: 'bye', id: this.doc.clientID }));
      this.bc?.close();
    } catch { /* already closed */ }
    this.bc = null;
    this.bcPeers.clear();
    if (!this.mesh && !this.floor) { this.emit('status', [{ status: 'disconnected' }]); return; }
    this.mesh.close();
    this.mesh = null;
    // The floor: p2p's universal fallback. WebRTC cannot cross a symmetric-NAT
    // pair, and that is precisely the case where two windows on different
    // networks would otherwise never meet.
    this.floor = null;
    this.floorPeers = new Set();
    this.useMesh = useMesh;
    this.useFloor = useFloor;
    this.synced = false;
    // Everyone else's presence came over channels that are now gone.
    const others = [...this.awareness.getStates().keys()].filter((id) => id !== this.doc.clientID);
    awarenessProtocol.removeAwarenessStates(this.awareness, others, 'disconnect');
    this.emit('status', [{ status: 'disconnected' }]);
  }

  destroy() {
    this.disconnect();
    removeEventListener('beforeunload', this._onUnload);
    this.doc.off('update', this._onUpdate);
    this.awareness.off('update', this._onAwareness);
    super.destroy();
  }
}
